import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromDesignSystem from '@serious-system';
import { environment } from '../../environments/environment';
import { NAVIGATION_OPEN_QUERY_PARAM } from '../chats/shared/services/mobile-navigation.service';
import * as fromHelper from './login.helper';
import { AuthActions } from './store/auth.actions';
import { authFeature, AuthState } from './store/auth.reducer';

@Component({
  imports: [
    TranslateModule,
    RouterModule,
    fromDesignSystem.ButtonDirective,
    fromDesignSystem.UseSocialIconDirective,
    fromDesignSystem.UseLogoDirective,
  ],
  template: `
    <div class="w-dvw h-dvh flex flex-col bg-gradient-e-vertical">
      <div
        class="
        grow flex-col flex justify-center items-center
        px-8 py-10 tablet-landscape:pt-29
      "
      >
        <div class="flex flex-col items-center max-w-125">
          <!-- Intro message and logo -->
          <img
            sdUseLogo="logo-new"
            class="h-12 mb-10"
            [alt]="'LOGIN.COMPANY_LOGO_ALT' | translate"
          />
          <header
            class="
            flex flex-col items-center text-center
            tablet-landscape:gap-4 gap-3 mb-8
          "
          >
            <h1
              class="tablet-landscape:typo-h3 typo-h5 font-semibold text-neutral-900"
            >
              {{ 'LOGIN.TITLE' | translate }}
            </h1>
            <p class="typo-p1">
              {{ 'LOGIN.SUBTITLE' | translate }}
            </p>
          </header>

          <!-- Login buttons -->
          <div class="w-full max-w-100 grow gap-3 flex flex-col">
            @for (config of loginButtonConfigs; track config.idpCustomId) {
              <button
                sdButton
                variant="outline"
                color="neutral"
                (click)="handleClickLoginCTA(config.idpCustomId)"
              >
                <svg [sdUseSocialIcon]="config.idpLogo"></svg>
                @if (config.idpDomain) {
                  <span
                    class="truncate"
                    [innerHTML]="
                      'LOGIN.CTA_CONTINUE_WITH_DOMAIN'
                        | translate: { label: config.idpDomain }
                    "
                  >
                  </span>
                } @else {
                  <span class="truncate font-semibold">{{
                    'LOGIN.CTA_CONTINUE_WITH'
                      | translate: { label: config.idpLabel }
                  }}</span>
                }
              </button>
            }
          </div>
        </div>
      </div>

      <!-- Powered by squadbox footer -->
      <div
        class="flex justify-center items-end gap-2 pb-6 tablet-landscape:pb-8 pt-3"
      >
        <p class="text-neutral-500 typo-caption">
          {{ 'LOGIN.FOOTER_TEXT' | translate }}
        </p>
        <img
          sdUseLogo="logo-squadbox"
          class="tablet-landscape:h-8 h-6"
          [alt]="'LOGIN.SQUADBOX_LOGO_ALT' | translate"
        />
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage implements OnInit {
  private readonly authStore = inject<Store<AuthState>>(Store);
  private readonly user = this.authStore.selectSignal(authFeature.selectUser);
  private readonly router = inject(Router);
  // Declared before `loginButtonConfigs`: TS initializes fields top-to-bottom and
  // the list below reads `this.route`.
  private readonly route = inject(ActivatedRoute);

  public readonly loginButtonConfigs: fromHelper.LoginButtonConfig[] = [
    ...fromHelper.cognitoIdpConfigToLoginButtonConfigs(
      environment.cognitoIdpConfig,
    ),
    // SB-3337 T4: append the HS support button only when the URL carries the HS
    // IdP marker (KD-4/KD-6). Client buttons above are untouched (no filter).
    ...fromHelper.hsLoginButtonConfigIfRequested(
      this.route.snapshot.queryParamMap.get('identityProvider'),
      environment.hsIdpCustomId,
    ),
  ];

  constructor() {
    effect(() => {
      if (this.user()) {
        void this.router.navigate(['/chats/teammates'], {
          queryParams: { [NAVIGATION_OPEN_QUERY_PARAM]: true },
        });
      }
    });
  }

  ngOnInit(): void {
    this.authStore.dispatch(AuthActions.authenticateUser());
  }

  handleClickLoginCTA(idpCustomId: string) {
    this.authStore.dispatch(AuthActions.login({ idpCustomId }));
  }
}
